import {useMutation, useQueryClient, useQuery} from '@github-ui/react-query'
import {
  COPILOT_SPACES_QUERY_KEY,
  copilotSpacesQueryKey,
  deleteCopilotSpace,
  fetchCopilotSpace,
  fetchCopilotSpaces,
  fetchVisibilitySettings,
} from '../utils/fetch'
import type {
  CustomCopilotId,
  CustomCopilot,
  CustomCopilotPayload,
} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {useEffect} from 'react'
import {useChatStateValue} from '@github-ui/copilot-chat/CopilotChatContext'

export class CopilotSpaceNotFoundError extends Error {
  constructor(message = 'Copilot Space not found') {
    super(message)
    this.name = 'CopilotSpaceNotFoundError'
  }
}

export class CopilotSpaceSSOError extends Error {
  protectedOrganizations: string[]
  constructor(protectedOrganizations: string[], message = 'Copilot Space SSO error') {
    super(message)
    this.protectedOrganizations = protectedOrganizations
    this.name = 'CopilotSpaceSSOError'
  }
}

export function useDeleteCopilotSpace() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (item: CustomCopilotId) => {
      const result = await deleteCopilotSpace(item)
      // Ensure result is not an error type before returning
      if (!result.ok) {
        throw new Error(String(result.error))
      }
      // Only return the payload or a safe object
      return {status: result.status, ok: true, payload: result.payload}
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({queryKey: COPILOT_SPACES_QUERY_KEY})
    },
  })
}

/**
 * A hook to manage the custom copilots in the chat manager.
 * Run only for side effects. Eventually we'll replace this with the underlying
 * `useManagedFetchCopilotSpaces` hook.
 * It fetches the custom copilots and updates the chat manager state.
 * @param enabled - Whether to enable the fetching of custom copilots.
 */
export function useManagedFetchCopilotSpaces(enabled: boolean) {
  const manager = useChatManager()
  const {data} = useFetchCopilotSpaces(enabled)
  useEffect(() => {
    if (data) {
      manager.dispatch({type: 'SET_CUSTOM_COPILOTS', customCopilots: data})
    }
  }, [data, manager])
}
/**
 * The default hook for fetching custom copilots from the server.
 */
export function useFetchCopilotSpaces(enabled: boolean) {
  return useQuery({
    queryKey: COPILOT_SPACES_QUERY_KEY,
    queryFn: fetchCopilotSpaces,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

function hasCopilotSpaceData(payload: CustomCopilotPayload): payload is CustomCopilot {
  return (payload as CustomCopilot).id !== undefined
}

export function useFetchCopilotSpace(customCopilotId: CustomCopilotId | null) {
  return useQuery({
    queryKey: copilotSpacesQueryKey(customCopilotId),
    queryFn: async () => {
      const response = await fetchCopilotSpace(customCopilotId)
      if (!response.ok) {
        if (response.status === 404) {
          throw new CopilotSpaceNotFoundError()
        }
        throw new Error(response.error)
      }
      if (!hasCopilotSpaceData(response.payload)) {
        throw new CopilotSpaceSSOError(response.payload.protectedOrganizations)
      }
      return response.payload
    },
    enabled: !!customCopilotId,
  })
}

// We split up the customCopilotId because the linter wants each parameter used in the query key,
// but directly passing the whole object in seems to key on object identity and lead to lots of
// redundant API calls.
export function useFetchVisibilitySettings(copilotSpaceOwner: string, copilotSpaceNumber: number) {
  return useQuery({
    queryKey: ['visibility-settings', copilotSpaceOwner, copilotSpaceNumber],
    queryFn: async () => {
      return fetchVisibilitySettings({owner: copilotSpaceOwner, id: copilotSpaceNumber})
    },
    enabled: copilotSpaceOwner !== '' && copilotSpaceNumber !== -1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Custom copilots are enabled for:
 * 1. users with the :copilot_custom_copilots feature flag, which some will be individually opted into
 * 2. The following types of users with the :copilot_custom_copilots_feature_preview feature flag:
 *    a. users with Copilot Individual (CI) licenses, for whom preview features are always enabled
 *    b. CB and CE users whose orgs have opted into preview features
 *    c. Copilot Free users, signified by `has_limited_access` on their plan
 * This values is serialized down from the copilot_chat_helper and put into the chat state.
 */
export function useCustomCopilotsEnabled() {
  return useChatStateValue('customCopilotsEnabled')
}
